import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ActivityFeed = ({ language, direction }) => {
  const navigate = useNavigate();

  const activities = [
    {
      id: 1,
      type: 'case_update',
      titleEn: 'Case Status Updated',
      titleFr: 'Statut du Dossier Mis à Jour',
      titleAr: 'تم تحديث حالة القضية',
      descriptionEn: 'Case #2024-156 moved to "In Progress"',
      descriptionFr: 'Dossier #2024-156 déplacé vers "En cours"',
      descriptionAr: 'تم نقل القضية #2024-156 إلى "قيد التنفيذ"',
      user: 'Ahmed Benali',
      timestamp: '2026-01-27T17:30:00',
      icon: 'Briefcase',
      color: 'bg-primary/10 text-primary'
    },
    {
      id: 2,
      type: 'payment',
      titleEn: 'Payment Received',
      titleFr: 'Paiement Reçu',
      titleAr: 'تم استلام الدفع',
      descriptionEn: 'Invoice #INV-2024-089 paid - 15,000 MAD',
      descriptionFr: 'Facture #INV-2024-089 payée - 15 000 MAD',
      descriptionAr: 'تم دفع الفاتورة #INV-2024-089 - 15,000 درهم',
      user: 'Fatima Zahra',
      timestamp: '2026-01-27T16:15:00',
      icon: 'Receipt',
      color: 'bg-success/10 text-success'
    },
    {
      id: 3,
      type: 'document',
      titleEn: 'Document Uploaded',
      titleFr: 'Document Téléchargé',
      titleAr: 'تم تحميل المستند',
      descriptionEn: 'Contract agreement added to Case #2024-178',
      descriptionFr: 'Accord de contrat ajouté au Dossier #2024-178',
      descriptionAr: 'تمت إضافة اتفاقية العقد إلى القضية #2024-178',
      user: 'Youssef Alami',
      timestamp: '2026-01-27T15:45:00',
      icon: 'FileText',
      color: 'bg-warning/10 text-warning'
    },
    {
      id: 4,
      type: 'client',
      titleEn: 'New Client Added',
      titleFr: 'Nouveau Client Ajouté',
      titleAr: 'تمت إضافة عميل جديد',
      descriptionEn: 'Karim Idrissi registered as new client',
      descriptionFr: 'Karim Idrissi enregistré comme nouveau client',
      descriptionAr: 'تم تسجيل كريم الإدريسي كعميل جديد',
      user: 'Samira Bennani',
      timestamp: '2026-01-27T14:20:00',
      icon: 'UserPlus',
      color: 'bg-accent/10 text-accent'
    },
    {
      id: 5,
      type: 'hearing',
      titleEn: 'Hearing Scheduled',
      titleFr: 'Audience Planifiée',
      titleAr: 'تم جدولة الجلسة',
      descriptionEn: 'Court hearing set for Case #2024-145 on Feb 5',
      descriptionFr: 'Audience fixée pour Dossier #2024-145 le 5 fév',
      descriptionAr: 'تم تحديد جلسة المحكمة للقضية #2024-145 في 5 فبراير',
      user: 'Mohammed Tazi',
      timestamp: '2026-01-27T13:10:00',
      icon: 'Calendar',
      color: 'bg-error/10 text-error'
    },
    {
      id: 6,
      type: 'document',
      titleEn: 'Document Generated',
      titleFr: 'Document Généré',
      titleAr: 'تم إنشاء المستند',
      descriptionEn: 'Legal notice created for Case #2024-134',
      descriptionFr: 'Avis juridique créé pour Dossier #2024-134',
      descriptionAr: 'تم إنشاء إشعار قانوني للقضية #2024-134',
      user: 'Laila Mansouri',
      timestamp: '2026-01-27T11:30:00',
      icon: 'FileCheck',
      color: 'bg-primary/10 text-primary'
    }
  ];

  const getTitle = (activity) => {
    if (language === 'fr') return activity?.titleFr;
    if (language === 'ar') return activity?.titleAr;
    return activity?.titleEn;
  };

  const getDescription = (activity) => {
    if (language === 'fr') return activity?.descriptionFr;
    if (language === 'ar') return activity?.descriptionAr;
    return activity?.descriptionEn;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) {
      if (language === 'fr') return `Il y a ${diffMins} min`;
      if (language === 'ar') return `منذ ${diffMins} دقيقة`;
      return `${diffMins} min ago`;
    } else if (diffHours < 24) {
      if (language === 'fr') return `Il y a ${diffHours}h`;
      if (language === 'ar') return `منذ ${diffHours} ساعة`;
      return `${diffHours}h ago`;
    } else {
      if (language === 'ar') {
        return new Intl.DateTimeFormat('ar-MA', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })?.format(date);
      }
      return new Intl.DateTimeFormat('fr-MA', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })?.format(date);
    }
  };

  const titleLabel = {
    en: 'Recent Activity',
    fr: 'Activité Récente',
    ar: 'النشاط الأخير'
  };

  const viewAllLabel = {
    en: 'View All',
    fr: 'Voir Tout',
    ar: 'عرض الكل'
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">
          {titleLabel?.[language]}
        </h2>
        <button className="text-sm text-primary hover:text-primary/80 transition-colors">
          {viewAllLabel?.[language]}
        </button>
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {activities?.map((activity) => (
          <div
            key={activity?.id}
            className="p-4 hover:bg-muted transition-colors cursor-pointer"
            onClick={() => {
              if (activity?.type === 'case_update' || activity?.type === 'hearing') {
                navigate('/case-management');
              } else if (activity?.type === 'payment') {
                navigate('/billing-invoice');
              } else if (activity?.type === 'document') {
                navigate('/document-management');
              } else if (activity?.type === 'client') {
                navigate('/client-management');
              }
            }}
          >
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${activity?.color}`}
                >
                  <Icon name={activity?.icon} size={18} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">
                    {getTitle(activity)}
                  </p>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(activity?.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {getDescription(activity)}
                </p>
                <div className="flex items-center gap-2">
                  <Icon name="User" size={12} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {activity?.user}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityFeed;